import { useParams, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback } from 'react';
import Card from '../../../../components/base/Card';
import Button from '../../../../components/base/Button';

interface DrawingPath {
  id: string;
  points: { x: number; y: number }[];
  color: string;
  width: number;
  tool: 'pen' | 'eraser' | 'marker' | 'brush' | 'crayon';
  timestamp: number;
}

interface UploadedImage {
  id: string;
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  timestamp: number;
}

interface WhiteboardPage {
  id: string;
  name: string;
  backgroundImage?: string;
  paths: DrawingPath[];
  stickers: { id: string; emoji: string; x: number; y: number; size: number }[];
  uploadedImages: UploadedImage[];
}

interface WhiteboardState {
  pages: WhiteboardPage[];
  currentPageId: string;
}

interface OnlineUser {
  id: string;
  name: string;
  color: string;
  isTeacher: boolean;
  lastSeen: number;
}

const colors = [
  '#000000', '#FF4444', '#44FF44', '#4444FF', '#FFFF44', 
  '#FF44FF', '#44FFFF', '#FF8844', '#8844FF', '#FF88CC',
  '#88FF44', '#4488FF', '#FF4488', '#88FFFF', '#FFAA44',
  '#AA44FF', '#44AAFF', '#FF44AA', '#AAFF44', '#FFB366'
];

const brushSizes = [2, 4, 6, 8, 12, 16, 20, 24];

const emojis = [
  '😀', '😍', '🤔', '👍', '👎', '❤️', '⭐', '🎉', 
  '💡', '🔥', '✅', '❌', '➡️', '⬆️', '⬇️', '⬅️',
  '🎨', '📝', '🎯', '💯', '🚀', '🌟', '🎊', '🎁',
  '🌈', '☀️', '🌙', '⚡', '💎', '🏆', '🎪', '🎭'
];

const tools = [
  { 
    id: 'pen', 
    icon: 'ri-pencil-line', 
    name: 'Pencil', 
    color: '#2d5a35',
    image: 'https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/02e7443816047c38da7d4c6a19e07695.png'
  },
  { 
    id: 'marker', 
    icon: 'ri-mark-pen-line', 
    name: 'Pen', 
    color: '#e74c3c',
    image: 'https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/878e39d9cb79a89a4d32ade2f23dda92.png'
  },
  { 
    id: 'brush', 
    icon: 'ri-brush-line', 
    name: 'Airbrush', 
    color: '#9b59b6',
    image: 'https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/840265b2646539baf1236c7a2f9f76c0.png'
  },
  { 
    id: 'crayon', 
    icon: 'ri-edit-line', 
    name: 'Crayon', 
    color: '#f39c12',
    image: 'https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/e573d18d6734e6616e867119c821c686.png'
  },
  { 
    id: 'eraser', 
    icon: 'ri-eraser-line', 
    name: 'Eraser', 
    color: '#95a5a6',
    image: 'https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/776ac8ec5e5ce7306e0d5d93b590c6bb.png'
  },
  { 
    id: 'emoji', 
    icon: 'ri-emotion-line', 
    name: 'Emoji', 
    color: '#f39c12',
    emoji: '😀'
  }
];

export default function WhiteboardEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState<'pen' | 'eraser' | 'marker' | 'brush' | 'crayon' | 'emoji'>('pen');
  const [currentColor, setCurrentColor] = useState('#2d5a35');
  const [currentSize, setCurrentSize] = useState(6);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [whiteboardState, setWhiteboardState] = useState<WhiteboardState>({
    pages: [{
      id: 'page-1',
      name: 'Page 1',
      paths: [],
      stickers: [],
      uploadedImages: []
    }],
    currentPageId: 'page-1'
  });
  const [currentPath, setCurrentPath] = useState<DrawingPath | null>(null);
  const [shareCode, setShareCode] = useState('');
  const [boardTitle, setBoardTitle] = useState('Creative Whiteboard');
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [selectedEmoji, setSelectedEmoji] = useState('😀');
  const [showPageMenu, setShowPageMenu] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState('');

  // Mock online users for demo
  useEffect(() => {
    const mockUsers: OnlineUser[] = [
      { id: '1', name: 'Ms. Johnson', color: '#2d5a35', isTeacher: true, lastSeen: Date.now() },
      { id: '2', name: 'Emma', color: '#e74c3c', isTeacher: false, lastSeen: Date.now() - 30000 },
      { id: '3', name: 'Noah', color: '#3498db', isTeacher: false, lastSeen: Date.now() - 10000 },
      { id: '4', name: 'Sophia', color: '#9b59b6', isTeacher: false, lastSeen: Date.now() - 5000 },
      { id: '5', name: 'Liam', color: '#f39c12', isTeacher: false, lastSeen: Date.now() - 2000 }
    ];
    setOnlineUsers(mockUsers);
  }, []);

  useEffect(() => {
    // Load whiteboard data
    const savedData = localStorage.getItem(`whiteboard-${id}`);
    if (savedData) {
      const loadedState = JSON.parse(savedData);
      // Ensure uploadedImages exists for backward compatibility
      if (loadedState.pages) {
        loadedState.pages = loadedState.pages.map((page: any) => ({
          ...page,
          uploadedImages: page.uploadedImages || []
        }));
      }
      setWhiteboardState(loadedState);
    }
    
    // Get board title and share code from whiteboards list
    const whiteboards = JSON.parse(localStorage.getItem('whiteboards') || '[]');
    const currentBoard = whiteboards.find((board: any) => board.id === id);
    if (currentBoard) {
      setShareCode(currentBoard.shareCode);
      setBoardTitle(currentBoard.title);
    } else {
      // For joined whiteboards
      setShareCode(id?.replace('shared-', '') || '');
      setBoardTitle(`Shared Board (${id?.replace('shared-', '') || ''})`);
    }
  }, [id]);

  const saveWhiteboardState = useCallback((state: WhiteboardState) => {
    localStorage.setItem(`whiteboard-${id}`, JSON.stringify(state));
  }, [id]);

  const getCurrentPage = () => {
    if (!whiteboardState?.pages || whiteboardState.pages.length === 0) {
      return {
        id: 'page-1',
        name: 'Page 1',
        paths: [],
        stickers: [],
        uploadedImages: []
      };
    }
    return whiteboardState.pages.find(page => page.id === whiteboardState.currentPageId) || whiteboardState.pages[0];
  };

  const updateCurrentPage = (updates: Partial<WhiteboardPage>) => {
    if (!whiteboardState?.pages) return;
    
    const newState = {
      ...whiteboardState,
      pages: whiteboardState.pages.map(page => 
        page.id === whiteboardState.currentPageId 
          ? { ...page, ...updates }
          : page
      )
    };
    setWhiteboardState(newState);
    saveWhiteboardState(newState);
  };

  const addNewPage = () => {
    const pageNumber = (whiteboardState?.pages?.length || 0) + 1;
    const newPage: WhiteboardPage = {
      id: `page-${Date.now()}`,
      name: `Page ${pageNumber}`,
      paths: [],
      stickers: [],
      uploadedImages: []
    };
    
    const currentPages = whiteboardState?.pages || [];
    const newState = {
      pages: [...currentPages, newPage],
      currentPageId: newPage.id
    };
    
    setWhiteboardState(newState);
    saveWhiteboardState(newState);
    setShowPageMenu(false);
  };

  const switchToPage = (pageId: string) => {
    if (!whiteboardState?.pages) return;
    
    setWhiteboardState(prev => ({
      ...prev,
      currentPageId: pageId
    }));
    setShowPageMenu(false);
    setSelectedImage(null); // Clear selection when switching pages
  };

  const deletePage = (pageId: string) => {
    if (!whiteboardState?.pages || whiteboardState.pages.length <= 1) return;
    
    const newPages = whiteboardState.pages.filter(page => page.id !== pageId);
    const newCurrentPageId = whiteboardState.currentPageId === pageId 
      ? newPages[0].id 
      : whiteboardState.currentPageId;
    
    const newState = {
      pages: newPages,
      currentPageId: newCurrentPageId
    };
    
    setWhiteboardState(newState);
    saveWhiteboardState(newState);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        
        // Create a new uploaded image object
        const newImage: UploadedImage = {
          id: Date.now().toString(),
          src: imageUrl,
          x: 100, // Default position
          y: 100,
          width: 200, // Default size
          height: 150,
          rotation: 0,
          timestamp: Date.now()
        };
        
        const currentPage = getCurrentPage();
        updateCurrentPage({ 
          uploadedImages: [...(currentPage.uploadedImages || []), newImage] 
        });
      };
      reader.readAsDataURL(file);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getImageAtPosition = (x: number, y: number): UploadedImage | null => {
    const currentPage = getCurrentPage();
    if (!currentPage.uploadedImages) return null;
    
    // Check images in reverse order (top to bottom)
    for (let i = currentPage.uploadedImages.length - 1; i >= 0; i--) {
      const img = currentPage.uploadedImages[i];
      if (x >= img.x && x <= img.x + img.width && 
          y >= img.y && y <= img.y + img.height) {
        return img;
      }
    }
    return null;
  };

  const updateImage = (imageId: string, updates: Partial<UploadedImage>) => {
    const currentPage = getCurrentPage();
    const updatedImages = (currentPage.uploadedImages || []).map(img => 
      img.id === imageId ? { ...img, ...updates } : img
    );
    updateCurrentPage({ uploadedImages: updatedImages });
  };

  const deleteSelectedImage = () => {
    if (!selectedImage) return;
    
    const currentPage = getCurrentPage();
    const updatedImages = (currentPage.uploadedImages || []).filter(img => img.id !== selectedImage);
    updateCurrentPage({ uploadedImages: updatedImages });
    setSelectedImage(null);
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const pos = getEventPos(e);
    
    // Check if we're clicking on an uploaded image
    const clickedImage = getImageAtPosition(pos.x, pos.y);
    if (clickedImage) {
      e.preventDefault();
      e.stopPropagation();
      setSelectedImage(clickedImage.id);
      setDragOffset({
        x: pos.x - clickedImage.x,
        y: pos.y - clickedImage.y
      });
      setIsDragging(true);
      return;
    }
    
    // Clear image selection if clicking elsewhere
    setSelectedImage(null);
    
    // If emoji tool is selected, place emoji instead of drawing
    if (currentTool === 'emoji') {
      addStickerAtPosition(selectedEmoji, pos.x, pos.y);
      return;
    }

    setIsDrawing(true);
    
    const newPath: DrawingPath = {
      id: Date.now().toString(),
      points: [pos],
      color: currentColor,
      width: currentSize,
      tool: currentTool as 'pen' | 'eraser' | 'marker' | 'brush' | 'crayon',
      timestamp: Date.now()
    };
    
    setCurrentPath(newPath);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    const pos = getEventPos(e);
    
    // Handle image dragging - prevent other drawing when dragging image
    if (isDragging && selectedImage) {
      e.preventDefault();
      e.stopPropagation();
      updateImage(selectedImage, {
        x: Math.max(0, Math.min(pos.x - dragOffset.x, 800)), // Keep within canvas bounds
        y: Math.max(0, Math.min(pos.y - dragOffset.y, 600))
      });
      return;
    }
    
    if (!isDrawing || !currentPath || currentTool === 'emoji') return;
    
    const updatedPath = {
      ...currentPath,
      points: [...currentPath.points, pos]
    };
    
    setCurrentPath(updatedPath);
    
    // Draw current stroke
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    
    ctx.beginPath();
    ctx.strokeStyle = currentTool === 'eraser' ? '#FFFFFF' : currentColor;
    ctx.lineWidth = currentSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Apply tool effects with more distinct differences
    switch (currentTool) {
      case 'eraser':
        ctx.globalCompositeOperation = 'destination-out';
        break;
      case 'marker':
        // Pen - clean, sharp lines
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.lineCap = 'round';
        break;
      case 'brush':
        // Airbrush - soft, spray-like effect
        ctx.globalAlpha = 0.3;
        ctx.globalCompositeOperation = 'source-over';
        ctx.lineCap = 'round';
        ctx.shadowBlur = currentSize * 0.5;
        ctx.shadowColor = currentColor;
        break;
      case 'crayon':
        // Crayon - textured, waxy effect
        ctx.globalAlpha = 0.8;
        ctx.globalCompositeOperation = 'source-over';
        ctx.lineCap = 'square';
        break;
      case 'pen':
        // Pencil - light, sketchy lines
        ctx.globalAlpha = 0.7;
        ctx.globalCompositeOperation = 'source-over';
        ctx.lineCap = 'round';
        break;
      default:
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
    }
    
    const points = updatedPath.points;
    if (points.length >= 2) {
      const lastPoint = points[points.length - 2];
      const currentPoint = points[points.length - 1];
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(currentPoint.x, currentPoint.y);
      ctx.stroke();
    }
    
    // Reset effects
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  };

  const stopDrawing = () => {
    if (isDragging) {
      setIsDragging(false);
      return;
    }
    
    if (!isDrawing || !currentPath || currentTool === 'emoji') return;
    
    setIsDrawing(false);
    
    const currentPage = getCurrentPage();
    updateCurrentPage({ paths: [...currentPage.paths, currentPath] });
    setCurrentPath(null);
  };

  const undo = () => {
    const currentPage = getCurrentPage();
    if (!currentPage || (!currentPage.paths?.length && !currentPage.stickers?.length && !currentPage.uploadedImages?.length)) return;
    
    // Remove the most recent item (path, sticker, or image)
    const lastPathTime = currentPage.paths?.length > 0 ? currentPage.paths[currentPage.paths.length - 1].timestamp : 0;
    const lastStickerTime = currentPage.stickers?.length > 0 ? parseInt(currentPage.stickers[currentPage.stickers.length - 1].id) : 0;
    const lastImageTime = currentPage.uploadedImages?.length > 0 ? currentPage.uploadedImages[currentPage.uploadedImages.length - 1].timestamp : 0;
    
    const maxTime = Math.max(lastPathTime, lastStickerTime, lastImageTime);
    
    if (maxTime === lastPathTime) {
      updateCurrentPage({ paths: currentPage.paths.slice(0, -1) });
    } else if (maxTime === lastStickerTime) {
      updateCurrentPage({ stickers: currentPage.stickers.slice(0, -1) });
    } else {
      updateCurrentPage({ uploadedImages: currentPage.uploadedImages?.slice(0, -1) || [] });
    }
  };

  const addStickerAtPosition = (emoji: string, x: number, y: number) => {
    const newSticker = {
      id: Date.now().toString(),
      emoji,
      x,
      y,
      size: 32
    };
    
    const currentPage = getCurrentPage();
    if (!currentPage) return;
    
    updateCurrentPage({ stickers: [...(currentPage.stickers || []), newSticker] });
  };

  const addSticker = (emoji: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEmoji(emoji);
    setCurrentTool('emoji');
    setShowEmojiPicker(false);
  };

  const clearCurrentPage = () => {
    const currentPage = getCurrentPage();
    if (!currentPage) return;
    
    updateCurrentPage({ 
      paths: [], 
      stickers: [], 
      uploadedImages: [] 
    });
    setSelectedImage(null);
  };

  const clearBackground = () => {
    updateCurrentPage({ backgroundImage: undefined });
  };

  const copyShareCode = () => {
    navigator.clipboard.writeText(shareCode);
  };

  const closeAllMenus = () => {
    setShowColorPicker(false);
    setShowSizePicker(false);
    setShowEmojiPicker(false);
    setShowPageMenu(false);
  };

  const getEventPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const currentPage = getCurrentPage();
    if (!currentPage) return;
    
    // Draw background image if exists
    if (currentPage.backgroundImage) {
      const img = new Image();
      img.onload = () => {
        ctx.globalAlpha = 0.8;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = 1;
        drawContent();
      };
      img.src = currentPage.backgroundImage;
    } else {
      drawContent();
    }
    
    function drawContent() {
      // Draw uploaded images first (behind drawings)
      if (currentPage.uploadedImages) {
        currentPage.uploadedImages.forEach(imgData => {
          const img = new Image();
          img.onload = () => {
            ctx.save();
            ctx.translate(imgData.x + imgData.width/2, imgData.y + imgData.height/2);
            ctx.rotate(imgData.rotation * Math.PI / 180);
            ctx.drawImage(img, -imgData.width/2, -imgData.height/2, imgData.width, imgData.height);
            ctx.restore();
          };
          img.src = imgData.src;
        });
      }
      
      // Draw all paths
      if (currentPage?.paths) {
        currentPage.paths.forEach(path => {
          if (path.points.length < 2) return;
          
          ctx.beginPath();
          ctx.strokeStyle = path.tool === 'eraser' ? '#FFFFFF' : path.color;
          ctx.lineWidth = path.width;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          // Different tool effects with more distinct differences
          switch (path.tool) {
            case 'eraser':
              ctx.globalCompositeOperation = 'destination-out';
              break;
            case 'marker':
              // Pen - clean, sharp lines
              ctx.globalAlpha = 1;
              ctx.globalCompositeOperation = 'source-over';
              ctx.lineCap = 'round';
              break;
            case 'brush':
              // Airbrush - soft, spray-like effect
              ctx.globalAlpha = 0.3;
              ctx.globalCompositeOperation = 'source-over';
              ctx.lineCap = 'round';
              ctx.shadowBlur = path.width * 0.5;
              ctx.shadowColor = path.color;
              break;
            case 'crayon':
              // Crayon - textured, waxy effect
              ctx.globalAlpha = 0.8;
              ctx.globalCompositeOperation = 'source-over';
              ctx.lineCap = 'square';
              break;
            case 'pen':
              // Pencil - light, sketchy lines
              ctx.globalAlpha = 0.7;
              ctx.globalCompositeOperation = 'source-over';
              ctx.lineCap = 'round';
              break;
            default:
              ctx.globalAlpha = 1;
              ctx.globalCompositeOperation = 'source-over';
          }
          
          ctx.moveTo(path.points[0].x, path.points[0].y);
          path.points.forEach(point => {
            ctx.lineTo(point.x, point.y);
          });
          ctx.stroke();
          
          // Reset effects
          ctx.globalAlpha = 1;
          ctx.shadowBlur = 0;
        });
      }
      
      // Reset composite operation
      ctx.globalCompositeOperation = 'source-over';
    }
  }, [whiteboardState]);

  const currentPage = getCurrentPage();
  const selectedImageData = selectedImage ? currentPage.uploadedImages?.find(img => img.id === selectedImage) : null;

  return (
    <div className="h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex overflow-hidden">
      {/* Main Content - Now full width */}
      <div className="flex-1 flex flex-col">
        {/* Header with User Presence - Fixed height */}
        <div className="bg-white/95 backdrop-blur-sm shadow-lg border-b border-purple-200 p-4 z-40 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/whiteboard')}
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-100 p-2 rounded-full transition-all"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {boardTitle} - {currentPage.name}
              </h1>
              {shareCode && (
                <div className="flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-pink-100 px-4 py-2 rounded-full border border-purple-200">
                  <span className="text-sm text-purple-700 font-medium">Code: {shareCode}</span>
                  <button 
                    onClick={copyShareCode}
                    className="text-purple-600 hover:text-purple-800 hover:bg-purple-200 p-1 rounded-full transition-all"
                  >
                    <i className="ri-clipboard-line"></i>
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Image Controls - Show when image is selected */}
              {selectedImage && selectedImageData && (
                <div className="flex items-center space-x-2 bg-orange-100 px-3 py-2 rounded-full border border-orange-200">
                  <button
                    onClick={() => updateImage(selectedImage, { width: selectedImageData.width + 20, height: selectedImageData.height + 15 })}
                    className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded-full transition-all"
                    title="Increase size"
                  >
                    <i className="ri-zoom-in-line"></i>
                  </button>
                  <button
                    onClick={() => updateImage(selectedImage, { width: Math.max(50, selectedImageData.width - 20), height: Math.max(40, selectedImageData.height - 15) })}
                    className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded-full transition-all"
                    title="Decrease size"
                  >
                    <i className="ri-zoom-out-line"></i>
                  </button>
                  <button
                    onClick={() => updateImage(selectedImage, { rotation: (selectedImageData.rotation + 90) % 360 })}
                    className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded-full transition-all"
                    title="Rotate"
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                  <button
                    onClick={deleteSelectedImage}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-all"
                    title="Delete image"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              )}
              
              {/* Page Navigation Controls */}
              <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-3 py-2 rounded-full border border-purple-200">
                <button
                  onClick={() => {
                    const currentIndex = whiteboardState?.pages?.findIndex(page => page.id === whiteboardState.currentPageId) || 0;
                    if (currentIndex > 0) {
                      switchToPage(whiteboardState.pages[currentIndex - 1].id);
                    }
                  }}
                  disabled={!whiteboardState?.pages || whiteboardState.pages.findIndex(page => page.id === whiteboardState.currentPageId) === 0}
                  className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Previous page"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                
                <span className="text-sm font-medium text-purple-700 px-2">
                  Page {(whiteboardState?.pages?.findIndex(page => page.id === whiteboardState.currentPageId) || 0) + 1} of {whiteboardState?.pages?.length || 1}
                </span>
                
                <button
                  onClick={() => {
                    const currentIndex = whiteboardState?.pages?.findIndex(page => page.id === whiteboardState.currentPageId) || 0;
                    if (currentIndex < (whiteboardState?.pages?.length || 0) - 1) {
                      switchToPage(whiteboardState.pages[currentIndex + 1].id);
                    }
                  }}
                  disabled={!whiteboardState?.pages || whiteboardState.pages.findIndex(page => page.id === whiteboardState.currentPageId) === (whiteboardState.pages.length - 1)}
                  className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Next page"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </div>
              
              {/* Clear Background Button */}
              {currentPage.backgroundImage && (
                <button 
                  onClick={clearBackground}
                  className="p-3 text-orange-500 hover:text-orange-700 hover:bg-orange-100 rounded-full transition-all shadow-md"
                  title="Remove background image"
                >
                  <i className="ri-image-line text-lg"></i>
                </button>
              )}
              
              {/* Clear Page Button */}
              <button 
                onClick={clearCurrentPage}
                className="p-3 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-all shadow-md"
                title="Clear current page"
                disabled={!currentPage?.paths?.length && !currentPage?.stickers?.length && !currentPage?.uploadedImages?.length}
              >
                <i className="ri-eraser-line text-lg"></i>
              </button>
              
              <button 
                onClick={undo}
                className="p-3 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full transition-all shadow-md"
                disabled={!currentPage?.paths?.length && !currentPage?.stickers?.length && !currentPage?.uploadedImages?.length}
              >
                <i className="ri-arrow-go-back-line text-lg"></i>
              </button>
            </div>
          </div>

          {/* Online Users Bar */}
          <div className="mt-4 flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <i className="ri-user-line"></i>
              <span className="font-medium">Online ({onlineUsers.length}):</span>
            </div>
            <div className="flex items-center space-x-2 flex-wrap">
              {onlineUsers.slice(0, 8).map((user) => (
                <div 
                  key={user.id}
                  className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full border shadow-sm"
                  title={`${user.name} ${user.isTeacher ? '(Teacher)' : '(Student)'}`}
                >
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: user.color }}
                  />
                  <span className="text-xs font-medium text-gray-700 max-w-20 truncate">
                    {user.name}
                  </span>
                  {user.isTeacher && (
                    <i className="ri-graduation-cap-fill text-xs text-purple-600"></i>
                  )}
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" title="Online"></div>
                </div>
              ))}
              {onlineUsers.length > 8 && (
                <div className="bg-gray-100 px-2 py-1 rounded-full text-xs text-gray-500">
                  +{onlineUsers.length - 8} more
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Canvas Area - Takes remaining space */}
        <div 
          className="flex-1 relative"
          onClick={closeAllMenus}
        >
          <div className="absolute inset-0 m-2 bg-white rounded-2xl shadow-2xl border border-purple-200 overflow-hidden">
            <canvas
              ref={canvasRef}
              className={`w-full h-full touch-none rounded-2xl ${
                currentTool === 'emoji' ? 'cursor-pointer' : 'cursor-crosshair'
              }`}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={(e) => {
                e.preventDefault();
                startDrawing(e);
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                draw(e);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                stopDrawing();
              }}
            />
            
            {/* Stickers Overlay */}
            {currentPage?.stickers?.map(sticker => (
              <div
                key={sticker.id}
                className="absolute pointer-events-none select-none drop-shadow-lg"
                style={{ 
                  left: sticker.x - sticker.size/2, 
                  top: sticker.y - sticker.size/2,
                  fontSize: sticker.size
                }}
              >
                {sticker.emoji}
              </div>
            ))}
            
            {/* Uploaded Images Overlay with Selection */}
            {currentPage?.uploadedImages?.map(imgData => (
              <div
                key={imgData.id}
                className={`absolute select-none ${
                  selectedImage === imgData.id ? 'ring-2 ring-orange-400 ring-offset-2 z-10' : 'z-0'
                }`}
                style={{ 
                  left: imgData.x, 
                  top: imgData.y,
                  width: imgData.width,
                  height: imgData.height,
                  transform: `rotate(${imgData.rotation}deg)`,
                  transformOrigin: 'center center',
                  cursor: selectedImage === imgData.id ? 'move' : 'pointer'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const rect = canvasRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  
                  const pos = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                  };
                  
                  setSelectedImage(imgData.id);
                  setDragOffset({
                    x: pos.x - imgData.x,
                    y: pos.y - imgData.y
                  });
                  setIsDragging(true);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const rect = canvasRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  
                  const touch = e.touches[0];
                  const pos = {
                    x: touch.clientX - rect.left,
                    y: touch.clientY - rect.top
                  };
                  
                  setSelectedImage(imgData.id);
                  setDragOffset({
                    x: pos.x - imgData.x,
                    y: pos.y - imgData.y
                  });
                  setIsDragging(true);
                }}
              >
                <img 
                  src={imgData.src} 
                  alt="Uploaded content"
                  className="w-full h-full object-cover rounded-lg shadow-lg pointer-events-none"
                  draggable={false}
                />
                {selectedImage === imgData.id && (
                  <div className="absolute inset-0 border-2 border-orange-400 rounded-lg pointer-events-none">
                    {/* Resize handles */}
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-orange-400 rounded-full"></div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-400 rounded-full"></div>
                    <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-orange-400 rounded-full"></div>
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-orange-400 rounded-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Toolbar - Fixed height */}
        <div className="bg-white/95 backdrop-blur-sm border-t border-purple-200 p-4 shadow-lg z-50 flex-shrink-0">
          {/* Tool Selection - Properly centered without overflow issues */}
          <div className="flex justify-center mb-4">
            <div className="flex items-center space-x-3 bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-2xl border border-purple-200 shadow-inner">
              {tools.map(tool => (
                <div key={tool.id} className="relative group">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentTool(tool.id as any);
                      if (tool.id === 'emoji') {
                        setSelectedEmoji('😀');
                      }
                    }}
                    className={`p-3 rounded-xl transition-all transform hover:scale-105 shadow-lg relative overflow-hidden w-14 h-14 flex items-center justify-center ${
                      currentTool === tool.id 
                        ? 'ring-3 ring-offset-1 scale-105' 
                        : 'hover:shadow-xl'
                    }`}
                    style={{
                      backgroundColor: currentTool === tool.id ? tool.color : '#ffffff',
                      ringColor: tool.color
                    }}
                  >
                    {tool.id === 'emoji' ? (
                      <span className="text-2xl">😀</span>
                    ) : (
                      <img 
                        src={tool.image} 
                        alt={tool.name}
                        className="w-7 h-7 object-cover rounded-lg"
                        style={{ filter: currentTool === tool.id ? 'brightness(1.2) saturate(1.2)' : 'none' }}
                      />
                    )}
                    {currentTool === tool.id && (
                      <div className="absolute inset-0 bg-white/20 rounded-xl"></div>
                    )}
                  </button>
                  
                  {/* Hover Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    {tool.name}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              ))}
              
              {/* Upload Button */}
              <div className="relative group">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  className="p-3 rounded-xl transition-all transform hover:scale-105 shadow-lg bg-gradient-to-br from-indigo-100 to-blue-100 text-indigo-600 hover:from-indigo-200 hover:to-blue-200 border border-indigo-200 w-14 h-14 flex items-center justify-center"
                >
                  <i className="ri-upload-line text-xl"></i>
                </button>
                
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  Upload Image
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
              
              {/* Add Page Button */}
              <div className="relative group">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    addNewPage();
                  }}
                  className="p-3 rounded-xl transition-all transform hover:scale-105 shadow-lg bg-gradient-to-br from-green-100 to-emerald-100 text-green-600 hover:from-green-200 hover:to-emerald-200 border border-green-200 w-14 h-14 flex items-center justify-center"
                >
                  <i className="ri-add-line text-xl"></i>
                </button>
                
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  Add Page
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Control Section - Centered */}
          <div className="flex justify-center">
            <div className="flex items-center space-x-4">
              {/* Color Picker - Hidden for emoji tool */}
              {currentTool !== 'emoji' && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowColorPicker(!showColorPicker);
                      setShowSizePicker(false);
                      setShowEmojiPicker(false);
                    }}
                    className="w-12 h-12 rounded-full border-4 border-white shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 ring-3 ring-purple-200"
                    style={{ backgroundColor: currentColor }}
                  >
                    {currentColor === '#FFFFFF' && (
                      <i className="ri-palette-line text-gray-600 text-lg"></i>
                    )}
                  </button>
                  
                  {showColorPicker && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowColorPicker(false);
                        }}
                      ></div>
                      <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-2xl border border-purple-200 p-4 z-[100] w-64">
                        <h3 className="text-sm font-bold text-gray-700 mb-3 text-center">Choose Color</h3>
                        <div className="grid grid-cols-5 gap-2">
                          {colors.map(color => (
                            <button
                              key={color}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentColor(color);
                                setShowColorPicker(false);
                              }}
                              className="w-10 h-10 rounded-full border-2 border-white shadow-lg hover:scale-110 transition-transform hover:shadow-xl ring-2 ring-gray-200 hover:ring-purple-300"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Size Picker - Hidden for emoji tool */}
              {currentTool !== 'emoji' && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSizePicker(!showSizePicker);
                      setShowColorPicker(false);
                      setShowEmojiPicker(false);
                    }}
                    className="px-4 py-3 bg-gradient-to-r from-blue-100 to-purple-100 text-purple-600 hover:from-blue-200 hover:to-purple-200 rounded-2xl flex items-center space-x-2 shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                  >
                    <div 
                      className="rounded-full bg-current"
                      style={{ width: Math.min(currentSize, 16), height: Math.min(currentSize, 16) }}
                    />
                    <span className="text-xs font-bold">{currentSize}px</span>
                    <i className="ri-arrow-up-s-line text-sm"></i>
                  </button>
                  
                  {showSizePicker && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSizePicker(false);
                        }}
                      ></div>
                      <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-2xl border border-purple-200 p-4 z-[100]">
                        <h3 className="text-sm font-bold text-gray-700 mb-3 text-center">Brush Size</h3>
                        <div className="space-y-2 min-w-[120px]">
                          {brushSizes.map(size => (
                            <button
                              key={size}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentSize(size);
                                setShowSizePicker(false);
                              }}
                              className={`flex items-center justify-between p-2 hover:bg-purple-50 rounded-xl w-full transition-all hover:scale-105 ${
                                currentSize === size ? 'bg-purple-100 ring-2 ring-purple-300' : ''
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div 
                                  className="rounded-full bg-purple-600"
                                  style={{ width: size, height: size }}
                                />
                                <span className="text-sm font-bold">{size}px</span>
                              </div>
                              {currentSize === size && (
                                <i className="ri-check-line text-purple-600"></i>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Emoji Picker */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEmojiPicker(!showEmojiPicker);
                    setShowColorPicker(false);
                    setShowSizePicker(false);
                  }}
                  className={`px-4 py-3 rounded-2xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center space-x-2 ${
                    currentTool === 'emoji' 
                      ? 'bg-gradient-to-r from-yellow-200 to-orange-200 text-orange-700 ring-2 ring-orange-300' 
                      : 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-600 hover:from-yellow-200 hover:to-orange-200'
                  }`}
                >
                  <span className="text-lg">{currentTool === 'emoji' ? selectedEmoji : '😀'}</span>
                  {currentTool === 'emoji' && <span className="text-xs font-bold">Selected</span>}
                </button>
                
                {showEmojiPicker && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEmojiPicker(false);
                      }}
                    ></div>
                    <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-2xl border border-orange-200 p-4 z-[100]">
                      <h3 className="text-sm font-bold text-gray-700 mb-3 text-center">Choose Emoji</h3>
                      <div className="grid grid-cols-6 gap-2 w-64">
                        {emojis.map(emoji => (
                          <button
                            key={emoji}
                            onClick={(e) => {
                              e.stopPropagation();
                              addSticker(emoji, e);
                            }}
                            className={`w-10 h-10 text-lg rounded-xl transition-all transform hover:scale-110 flex items-center justify-center shadow-sm hover:shadow-md ${
                              selectedEmoji === emoji && currentTool === 'emoji'
                                ? 'bg-orange-100 ring-2 ring-orange-300'
                                : 'hover:bg-orange-50'
                            }`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
}
